import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { fetchCountries } from 'redux/users/actions';

import { Checkbox } from 'semantic-ui-react';
import Button from 'components/elements/buttons/Button';
import CommonModal from 'components/elements/modals/CommonModal';
import MultiSelect from 'components/elements/MultiSelect';
import { AlertCircle }from 'tabler-icons-react';

import './TurnOnEarnModal.css';

const maxCountries = 10; // set available country list count

const TurnOnEarnModal = ({
  turnOnConfirmModal,
  setTurnOnConfirmModal,
  onTurnOnEarn,
  fetchCountries,
  countries,
}) => {
  const { t } = useTranslation();
  const [pageImpressions, setPageImpressions] = useState(false);
  const [automatedClickingTools, setAutomatedClickingTools] = useState(false);
  const [illegalContent, setIllegalContent] = useState(false);
  const [terms, setTerms] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState([]);

  const _setPageImpressions = (_e, { checked }) => {
    setPageImpressions(checked);
  };
  const _setAutomatedClickingTools = (_e, { checked }) => {
    setAutomatedClickingTools(checked);
  };
  const _setIllegalContent = (_e, { checked }) => {
    setIllegalContent(checked);
  };
  const _setTerms = (_e, { checked }) => {
    setTerms(checked);
  };

  useEffect(() => {
    if (step === 2) {
      fetchCountries();
    }
  }, [step]);

  const handleCloseModal = () => {
    setTurnOnConfirmModal(false);
    setStep(1);
    setPageImpressions(false);
    setAutomatedClickingTools(false);
    setIllegalContent(false);
    setTerms(false);
    setSelectedCountry([]);
  };

  const renderFirstStep = () => {
    return (
      <div className="wrapper">
        <div className="text-content">
          <h3>
            <b>
              {t("By turning on advertising on your HeyLink.me page")}<br />
              {t("you agree to the following:")}
            </b>
          </h3>
          <p>
            <Checkbox
              label={
                <label>
                  <b>{t("Agree not to generate")}</b> {t("page impressions or clicks by clicking on your own live advertising placements")}
                </label>
              }
              onChange={_setPageImpressions}
              checked={pageImpressions}
            />
            <Checkbox
              label={
                <label>
                  <b>{t("Agree not to use")}</b> {t("automated clicking tools, traffic sources, robots or other deceptive software")}
                </label>
              }
              onChange={_setAutomatedClickingTools}
              checked={automatedClickingTools}
            />
            <Checkbox
              label={
                <label>
                  <b>{t("Agree not to generate")}</b> {t("any illegal, racy or pornographic content")}
                </label>
              }
              onChange={_setIllegalContent}
              checked={illegalContent}
            />
            <Checkbox
              label={
                <label>
                  <b>{t("Agree on")}</b> HeyLink.me <a href="https://heylink.me/terms/" target="_blank" rel="noreferrer">{t("Terms")}</a> & <a href="https://heylink.me/privacy/" target="_blank" rel="noreferrer">{t("Policies")}</a></label>
              }
              onChange={_setTerms}
              checked={terms}
            />
          </p>
          <p className="alert-text">
            <b><AlertCircle /></b>
            <b>{t("Your account will be permanently suspended if you breach one or more of our advertising guidelines")}</b>
          </p>
        </div>

        <div className="buttons-block">
          <Button
            className="modal-button confirm"
            onClick={() => { setStep(2); }}
            disabled={!pageImpressions || !automatedClickingTools || !illegalContent || !terms}
          >
            {t("Next")}
          </Button>
        </div>

      </div>
    );
  };

  const renderSecondStep = () => {
    const dropdownOptions = countries && countries.length > 0 ?
      countries.map(i => ({
        key: `country_id_${i.id}`,
        text: i.name,
        value: i.id,
      })) : [];

    const dropdownFilteredOptions = dropdownOptions && dropdownOptions.length > 0 ? dropdownOptions.filter(i => i && selectedCountry.indexOf(i.value) >= 0) : [];

    const applyBtnText = t("Apply for Review");

    return (
      <div className="wrapper">
        <div className="text-content">
          <h3>
            <b>{t("Please select your top audience countries")}<br /> {t("(max 10 countries)")}</b>
          </h3>
          <p className="info-text">
              {t("For example, if you are based in Australia, most likely your top audience country is Australia as well. You can select up to 10 countries. The more you select - the more monetisation opportunities you will have.")}
          </p>
          <p>
            <MultiSelect
              placeholder={t("Select your top audience (max 10 countries)")}
              className="earn-country-selector"
              loading={(dropdownOptions && dropdownOptions.length === 0) || false}
              fluid
              multiple
              search
              selection
              options={(selectedCountry && selectedCountry.length >= maxCountries) ? dropdownFilteredOptions.slice(0, dropdownFilteredOptions.length) : dropdownOptions}
              onChange={(_e, data) => { setSelectedCountry(data.value); }}
              noResultsMessage={(selectedCountry && selectedCountry.length >= maxCountries) ? t("You can select up to 10 countries.") : t("No results found.")}
            />
          </p>

          <p className="info-text">{t("Before you click")} &ldquo;{applyBtnText}&rdquo;, {t("please make sure you filled your profile. Good example is")} <a href="https://heylink.me/olga/" target="_blank" rel="noreferrer">{t("here")}</a>.</p>
        </div>

        <div className="buttons-block">
          <Button
            className="modal-button confirm"
            onClick={() => { onTurnOnEarn(selectedCountry); }}
            disabled={!selectedCountry || (selectedCountry && selectedCountry.length === 0) || (selectedCountry && selectedCountry.length > maxCountries)}
          >
            {applyBtnText}
          </Button>
        </div>
      </div>
    );
  };

  const renderModalSteps = stepContent => {
    switch(stepContent) {
      case 1:
        return renderFirstStep();
      case 2:
        return renderSecondStep();
      default:
        return renderFirstStep();
    }
  };

  return (
    <CommonModal
        className="earn-switcher-turn-on-confirm-modal__component"
        open={turnOnConfirmModal}
        onClose={() => { handleCloseModal(); }}
      >
        {renderModalSteps(step)}
      </CommonModal>
  );
};

TurnOnEarnModal.propTypes = {
  turnOnConfirmModal: PropTypes.bool,
  setTurnOnConfirmModal: PropTypes.func,
  onTurnOnEarn: PropTypes.func,
  fetchCountries: PropTypes.func,
  countries: PropTypes.array,
};
TurnOnEarnModal.defaultProps = {
  turnOnConfirmModal: false,
  setTurnOnConfirmModal: null,
  onTurnOnEarn: null,
  fetchCountries: null,
  countries: [],
};

const mapStateToProps = state => (
  {
    me: state.usersReducer.me,
    countries: state.usersReducer.countries,
  }
);

export default connect(
  mapStateToProps,
  {
    fetchCountries,
  },
)(TurnOnEarnModal);
